import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Spin, message } from 'antd';
import classNames from 'classnames';
import { ArrowLeft, Printer } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import {
  getRecycleOrderConsignmentAgreement,
  signRecycleOrderConsignmentAgreement,
} from 'apis/oms';
import XiaoMaLogoSvg from 'components/xiao-ma-logo-svg';
import {
  ConsignmentAgreementQuery,
  ConsignmentAgreementSnsPlatform,
} from 'types/oms';
import formatTimeInTimezone from 'utils/formatTimeInTimezone';
import {
  CONSIGNMENT_AGREEMENT,
  CONSIGNMENT_AGREEMENT_SNS_OPTIONS,
  CONSIGNMENT_AGREEMENT_UI,
} from './copy';

const JAPAN_TIMEZONE = 'Asia/Tokyo';

const ConsignmentAgreementPage = () => {
  const { submissionId: submissionIdParam } = useParams<{
    submissionId: string;
  }>();
  const location = useLocation();
  const submissionId = Number(submissionIdParam);
  const validSubmissionId = Number.isInteger(submissionId) && submissionId > 0;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState<ConsignmentAgreementQuery | null>(null);
  const [snsPlatforms, setSnsPlatforms] = useState<
    ConsignmentAgreementSnsPlatform[]
  >([]);
  const [agreed, setAgreed] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const canvasBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!validSubmissionId) {
      setLoading(false);
      setData(null);
      return;
    }

    let active = true;
    setLoading(true);
    getRecycleOrderConsignmentAgreement(submissionId)
      .then((res) => {
        if (!active) return;
        setData(res);
        setSnsPlatforms(res.signed?.snsPlatforms || []);
        setAgreed(Boolean(res.signed));
        setEditing(!res.signed);
        setHasSignature(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [submissionId, validSubmissionId]);

  const signed = Boolean(data?.signed);
  const canEdit = Boolean(data?.signable) && (!signed || editing);
  const showSignaturePad = canEdit;

  useEffect(() => {
    const box = canvasBoxRef.current;
    if (!box || !showSignaturePad) return;
    const update = () => {
      const height = Math.min(
        560,
        Math.max(420, Math.round(window.innerHeight * 0.48)),
      );
      setCanvasSize({ width: box.clientWidth, height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [showSignaturePad, loading, editing]);

  useEffect(() => {
    setHasSignature(false);
  }, [canvasSize.width, canvasSize.height]);

  const backTo =
    (location.state as { from?: string } | null)?.from ||
    '/rrs/consignment-agreement-list';

  const toggleSns = (
    value: ConsignmentAgreementSnsPlatform,
    checked: boolean,
  ) => {
    if (value === 'all') {
      setSnsPlatforms(checked ? ['all'] : []);
      return;
    }
    setSnsPlatforms((current) => {
      const rest = current.filter((item) => item !== 'all' && item !== value);
      return checked ? [...rest, value] : rest;
    });
  };

  const canSubmit = agreed && snsPlatforms.length > 0 && hasSignature;

  const startEdit = () => {
    setEditing(true);
    setAgreed(true);
    setHasSignature(false);
    setSnsPlatforms(data?.signed?.snsPlatforms || []);
  };

  const cancelEdit = () => {
    setEditing(false);
    setAgreed(true);
    setHasSignature(false);
    setSnsPlatforms(data?.signed?.snsPlatforms || []);
  };

  const submit = async () => {
    if (!canSubmit) return;
    const signature = signatureRef.current;
    if (!signature || signature.isEmpty()) {
      setHasSignature(false);
      return;
    }
    setSubmitting(true);
    try {
      const next = await signRecycleOrderConsignmentAgreement({
        submissionId,
        agreed: true,
        snsPlatforms,
        signDataUrl: signature.getTrimmedCanvas().toDataURL('image/png'),
      });
      setData(next);
      setSnsPlatforms(next.signed?.snsPlatforms || []);
      setAgreed(true);
      setEditing(false);
      setHasSignature(false);
      message.success(
        signed
          ? CONSIGNMENT_AGREEMENT_UI.updateSuccess
          : CONSIGNMENT_AGREEMENT_UI.success,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = snsPlatforms.includes('all');

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4">
      <Link
        to={backTo}
        className="mb-2 inline-flex items-center gap-1 text-sm text-black"
      >
        <ArrowLeft className="h-4 w-4" />
        {CONSIGNMENT_AGREEMENT_UI.back}
      </Link>

      {loading ? (
        <div className="flex justify-center py-24">
          <Spin />
        </div>
      ) : !data ? (
        <div className="rounded-xl bg-neutral-50 px-4 py-8 text-center text-base">
          {CONSIGNMENT_AGREEMENT_UI.notReady}
        </div>
      ) : (
        <div className="space-y-8 text-neutral-900">
          <header className="text-center">
            <XiaoMaLogoSvg className="mx-auto h-20 w-auto" />
            <div className="mt-8 text-2xl font-medium tracking-wide">
              {CONSIGNMENT_AGREEMENT.title}
            </div>
          </header>

          <div className="text-right text-lg">
            {CONSIGNMENT_AGREEMENT_UI.bringInNo} {data.submissionId}
          </div>

          <article className="space-y-5 text-base leading-7">
            <p>{CONSIGNMENT_AGREEMENT.preamble}</p>
            {CONSIGNMENT_AGREEMENT.articles.map((article) => (
              <section key={article.no}>
                <h2 className="font-medium">
                  {article.no}条 {article.title}
                </h2>
                {article.paragraphs.map((paragraph, index) => (
                  <p key={`${article.no}-${index}`} className="mt-1">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </article>

          <section>
            <FieldLabel>{CONSIGNMENT_AGREEMENT_UI.sns}</FieldLabel>
            <div className="mb-3 text-sm text-neutral-500">
              {CONSIGNMENT_AGREEMENT_UI.snsMultiple}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CONSIGNMENT_AGREEMENT_SNS_OPTIONS.map((option) => {
                const selected =
                  option.value === 'all'
                    ? allSelected
                    : !allSelected && snsPlatforms.includes(option.value);
                const disabled = !canEdit;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleSns(option.value, !selected)}
                    className={classNames(
                      'min-h-[72px] rounded-xl border-2 px-4 text-lg font-medium',
                      selected
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-200 bg-white text-neutral-900',
                      disabled && 'cursor-default disabled:opacity-100',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>

          <label className="flex items-center gap-3 rounded-lg bg-[#f6f3ee] px-4 py-3 text-base">
            <input
              type="checkbox"
              className="h-5 w-5 accent-neutral-900"
              checked={agreed}
              disabled={!canEdit}
              onChange={(event) => setAgreed(event.target.checked)}
            />
            <span>{CONSIGNMENT_AGREEMENT.footer.consentLabel}</span>
          </label>

          {signed && !editing && data.signed ? (
            <section>
              <FieldLabel>{CONSIGNMENT_AGREEMENT_UI.signedAt}</FieldLabel>
              <ReadValue
                value={formatTimeInTimezone(
                  data.signed.signedAt,
                  JAPAN_TIMEZONE,
                )}
              />
              <img
                src={data.signed.signUrl}
                alt=""
                className="mx-auto mt-3 block max-h-48 mix-blend-multiply"
              />
            </section>
          ) : showSignaturePad ? (
            <section>
              <div
                ref={canvasBoxRef}
                className="overflow-hidden rounded-xl border-2 border-neutral-200 bg-white"
              >
                {canvasSize.width > 0 && (
                  <SignatureCanvas
                    key={`${canvasSize.width}x${canvasSize.height}`}
                    ref={signatureRef}
                    penColor="black"
                    onEnd={() =>
                      setHasSignature(!signatureRef.current?.isEmpty())
                    }
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: 'block',
                      style: { touchAction: 'none' },
                    }}
                  />
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 text-sm text-neutral-500">
                <span>{CONSIGNMENT_AGREEMENT_UI.hint}</span>
                {signed && editing && (
                  <button
                    type="button"
                    className="text-neutral-900 underline"
                    onClick={() => {
                      signatureRef.current?.clear();
                      setHasSignature(false);
                    }}
                  >
                    {CONSIGNMENT_AGREEMENT_UI.clear}
                  </button>
                )}
              </div>
            </section>
          ) : (
            <div className="rounded-xl bg-neutral-50 px-4 py-8 text-center text-base">
              {CONSIGNMENT_AGREEMENT_UI.notReady}
            </div>
          )}

          <div className="rounded-xl bg-neutral-50 px-4 py-4 text-sm leading-7 text-neutral-600">
            <div>{CONSIGNMENT_AGREEMENT.footer.shopName}</div>
            <div>{CONSIGNMENT_AGREEMENT.footer.address}</div>
            <div>{CONSIGNMENT_AGREEMENT.footer.tel}</div>
            <div>{CONSIGNMENT_AGREEMENT.footer.company}</div>
          </div>
        </div>
      )}

      {signed && !editing && data?.signed && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-neutral-200 bg-white p-4">
          <div className="mx-auto flex max-w-3xl gap-3">
            <Button
              className="h-14 flex-1 text-lg"
              icon={<Printer className="h-5 w-5" />}
              style={outlineButtonStyle}
              onClick={() =>
                window.open(
                  `/prints/consignment-agreement?submissionId=${data.submissionId}`,
                )
              }
            >
              {CONSIGNMENT_AGREEMENT_UI.print}
            </Button>
            <Button
              className="h-14 flex-1 text-lg"
              style={solidButtonStyle}
              onClick={startEdit}
            >
              {CONSIGNMENT_AGREEMENT_UI.edit}
            </Button>
          </div>
        </div>
      )}

      {showSignaturePad && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-neutral-200 bg-white p-4">
          <div className="mx-auto flex max-w-3xl gap-3">
            {signed && editing ? (
              <Button
                className="h-14 flex-1 text-lg"
                disabled={submitting}
                style={outlineButtonStyle}
                onClick={cancelEdit}
              >
                {CONSIGNMENT_AGREEMENT_UI.cancelEdit}
              </Button>
            ) : (
              <Button
                className="h-14 flex-1 text-lg"
                disabled={submitting}
                style={outlineButtonStyle}
                onClick={() => {
                  signatureRef.current?.clear();
                  setHasSignature(false);
                }}
              >
                {CONSIGNMENT_AGREEMENT_UI.clear}
              </Button>
            )}
            <Button
              loading={submitting}
              disabled={!canSubmit || submitting}
              className="h-14 flex-1 text-lg"
              style={canSubmit ? solidButtonStyle : disabledSolidButtonStyle}
              onClick={submit}
            >
              {CONSIGNMENT_AGREEMENT_UI.sign}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const outlineButtonStyle = {
  backgroundColor: '#fff',
  borderColor: '#171717',
  color: '#171717',
};

const solidButtonStyle = {
  backgroundColor: '#171717',
  borderColor: '#171717',
  color: '#fff',
};

const disabledSolidButtonStyle = {
  backgroundColor: '#e5e5e5',
  borderColor: '#e5e5e5',
  color: '#a3a3a3',
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <div className="mb-2 text-base font-medium text-neutral-700">{children}</div>
);

const ReadValue = ({ value }: { value?: string }) => (
  <div className="min-h-[56px] rounded-xl bg-neutral-50 px-4 py-3 text-lg">
    {value || '-'}
  </div>
);

export default ConsignmentAgreementPage;
